/*
  ЦИФРОВОЙ ДИАК ЗФ — PROJECT PAGE (FULLY EDITABLE MULTI-TOOL)
  Design: «Арктический Командный Центр» — Nornickel corporate style
  All passport sections are inline-editable with localStorage persistence.
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation } from 'wouter';
import {
  ArrowLeft, Target, Users, AlertTriangle,
  BarChart3, ListChecks, CalendarDays, BookOpen,
  ChevronDown, ChevronUp, GitBranch, Layers, Bot, Files, X, Table2,
} from 'lucide-react';
import { useProjects } from '@/contexts/ProjectContext';
import { InlineText, InlineDate, InlineSelect } from '@/components/InlineEdit';
import { RiskPanel } from '@/components/RiskPanel';
import { KPIPanel, StakeholderPanel, ScopePanel } from '@/components/EditablePanels';
import { DocumentsSection } from '@/components/DocumentsSection';
import { defaultProjectDocumentsConfig } from '@/store/projectStore';
import { EditableGantt } from '@/components/EditableGantt';
import { FactGanttViewer } from '@/components/FactGanttViewer';
import { KanbanBoard } from '@/components/KanbanBoard';
import { phaseOptionsFromRoadmap } from '@/data/phaseOptions';
import { cn } from '@/lib/utils';
import Sidebar from '@/components/Sidebar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  envelopesMismatch,
  formatRuDateRange,
  internalPlanEnvelope,
  mainGanttBarCount,
  mainGanttEnvelope,
  planOutsideRoadmap,
} from '@/lib/mainGanttProjectSpan';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Активный' },
  { value: 'planned', label: 'Запланировано' },
  { value: 'done', label: 'Завершён' },
] as const;

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  planned: 'bg-blue-100 text-blue-700',
  done: 'bg-gray-100 text-gray-600',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Активный',
  planned: 'Запланировано',
  done: 'Завершён',
};

const AI_COLORS: Record<string, string> = {
  high: '#00A651',
  medium: '#F5A623',
  needs_analysis: '#8E9BAE',
};

// ─── Collapsible Section ──────────────────────────────────────────────────

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  accent?: string;
  badge?: string;
  /** Плотнее шапка и отступы контента (для списков вроде KPI) */
  compact?: boolean;
}

function Section({ icon, title, children, defaultOpen = true, accent = '#0055A5', badge, compact }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
      <button
        onClick={() => setOpen(o => !o)}
        className={cn(
          'w-full flex items-center gap-3 hover:bg-muted/30 transition-colors text-left',
          compact ? 'px-4 py-2.5 gap-2.5' : 'px-5 py-3.5',
        )}
      >
        <span
          className={cn('rounded-lg shrink-0', compact ? 'p-1' : 'p-1.5')}
          style={{ backgroundColor: accent + '18' }}
        >
          <span style={{ color: accent }}>{icon}</span>
        </span>
        <span className={cn('font-semibold flex-1', compact ? 'text-[13px]' : 'text-sm')}>{title}</span>
        {badge && (
          <span
            className={cn(
              'rounded-full font-semibold mr-2',
              compact ? 'text-[10px] px-1.5 py-0' : 'text-xs px-2 py-0.5',
            )}
            style={{ backgroundColor: accent + '18', color: accent }}
          >
            {badge}
          </span>
        )}
        {open ? <ChevronUp size={15} className="text-muted-foreground shrink-0" /> : <ChevronDown size={15} className="text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <div className={compact ? 'px-4 pb-3 pt-0' : 'px-5 pb-5 pt-1'}>{children}</div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────

export default function ProjectPage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const {
    projects,
    ganttItems,
    roadmapPhases,
    canEdit,
    canSeeDocuments,
    updateProject,
    addScopeItem, updateScopeItem, removeScopeItem, reorderScopeItems, toggleScopeItemDone,
    addRisk, updateRisk, removeRisk,
    addKPI, updateKPI, removeKPI,
    addStakeholder, updateStakeholder, removeStakeholder,
    addGanttTask, updateGanttTask, removeGanttTask, reorderGanttTasksAfterDrag,
  } = useProjects();

  const ro = !canEdit;
  const project = projects.find(p => p.id === id);
  const phaseSelectOptions = phaseOptionsFromRoadmap(roadmapPhases);

  const roadmapEnv = useMemo(
    () => (project ? mainGanttEnvelope(project.id, ganttItems) : null),
    [project, ganttItems],
  );
  const planEnv = useMemo(
    () => (project ? internalPlanEnvelope(project.ganttTasks) : null),
    [project],
  );
  const mainBarCount = useMemo(
    () => (project ? mainGanttBarCount(project.id, ganttItems) : 0),
    [project, ganttItems],
  );
  // При нескольких полосах на общей дорожной карте сравнение по объединённому интервалу даёт
  // ложные срабатывания (каждая полоса — своя тематика/этап). В этом случае бьём тревогу,
  // только если детальный план реально вылезает за края объединённого интервала.
  const hasRoadmapMismatch = Boolean(
    project &&
      project.ganttTasks.length > 0 &&
      roadmapEnv &&
      planEnv &&
      (mainBarCount > 1
        ? planOutsideRoadmap(roadmapEnv, planEnv)
        : envelopesMismatch(roadmapEnv, planEnv)),
  );
  const dismissKey = project ? `diak.roadmapMismatchDismiss.${project.id}` : null;
  const dismissSignature =
    roadmapEnv && planEnv ? `${roadmapEnv.start}|${roadmapEnv.end}|${planEnv.start}|${planEnv.end}` : '';
  const [dismissedSignature, setDismissedSignature] = useState<string | null>(() => {
    if (typeof window === 'undefined' || !dismissKey) return null;
    return window.localStorage.getItem(dismissKey);
  });
  useEffect(() => {
    if (!dismissKey || typeof window === 'undefined') return;
    setDismissedSignature(window.localStorage.getItem(dismissKey));
  }, [dismissKey]);
  const showPlanRoadmapMismatch = hasRoadmapMismatch && dismissedSignature !== dismissSignature;
  const dismissMismatchBanner = () => {
    if (!dismissKey || typeof window === 'undefined' || !dismissSignature) return;
    window.localStorage.setItem(dismissKey, dismissSignature);
    setDismissedSignature(dismissSignature);
  };

  if (!project) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="flex min-h-screen flex-1 flex-col transition-[margin-left] duration-200 ease-out [margin-left:var(--app-sidebar-width)]">
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center">
              <p className="mb-4 text-muted-foreground">Проект не найден</p>
              <button type="button" onClick={() => navigate('/')} className="text-[var(--nn-blue)] hover:underline">
                ← На главную
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const progressColor = project.progress >= 70 ? '#00A651' : project.progress >= 30 ? '#F5A623' : '#0055A5';
  const lastUpdated = new Date(project.updatedAt).toLocaleDateString('ru-RU', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const openRisksCount = project.risks.filter(r => r.status === 'open').length;

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <div className="min-h-screen min-w-0 w-full max-w-none flex-1 bg-background transition-[margin-left] duration-200 ease-out [margin-left:var(--app-sidebar-width)]">
      {/* ── Sticky header ── */}
      <div className="sticky top-0 z-40 bg-card/95 backdrop-blur border-b border-border">
        <div className="w-full px-4 sm:px-6 h-13 flex items-center gap-3 py-2.5">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <ArrowLeft size={15} /> Все проекты
          </button>
          <div className="w-px h-4 bg-border" />
          <span className="font-semibold text-sm truncate flex-1 min-w-0">{project.title}</span>

          <InlineSelect
            value={project.status}
            options={STATUS_OPTIONS as unknown as { value: typeof project.status; label: string }[]}
            onSave={v => updateProject(project.id, { status: v })}
            readOnly={ro}
            renderValue={v => (
              <span className={cn('text-xs px-2.5 py-1 rounded-full font-semibold cursor-pointer', STATUS_STYLE[v])}>
                {STATUS_LABELS[v]}
              </span>
            )}
          />

          <span className="text-xs text-muted-foreground hidden md:block shrink-0">
            Сохранено: {lastUpdated}
          </span>

          {project.repoUrl && (
            <a
              href={project.repoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-[var(--nn-blue)] text-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)] transition-colors shrink-0"
            >
              <GitBranch size={12} /> Репозиторий
            </a>
          )}
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 py-5 space-y-4">

        {/* ── Hero card ── */}
        <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
          <div className="h-1 w-full" style={{ backgroundColor: '#0055A5' }} />
          <div className="p-5">
            <div className="flex items-start gap-6 flex-wrap">
              {/* Left: title + description */}
              <div className="flex-1 min-w-0 space-y-2">
                <div className="text-xs font-semibold text-[var(--nn-blue)] uppercase tracking-wide">
                  <InlineSelect
                    value={project.phaseId}
                    options={phaseSelectOptions as unknown as { value: string; label: string }[]}
                    onSave={(v) => {
                      const meta = roadmapPhases.find((p) => p.id === v);
                      updateProject(project.id, {
                        phaseId: v,
                        phaseLabel: meta ? `${meta.label} — ${meta.period}` : '',
                      });
                    }}
                    readOnly={ro}
                    renderValue={(v) => (
                      <span className="text-xs font-semibold text-[var(--nn-blue)] uppercase tracking-wide">
                        {phaseSelectOptions.find((o) => o.value === v)?.label ?? v}
                      </span>
                    )}
                  />
                </div>
                <InlineText
                  value={project.title}
                  onSave={v => updateProject(project.id, { title: v })}
                  className="text-2xl font-bold leading-tight"
                  inputClassName="text-2xl font-bold w-full"
                  displayAs="h1"
                  readOnly={ro}
                />
                <InlineText
                  value={project.description}
                  onSave={v => updateProject(project.id, { description: v })}
                  multiline
                  className="text-muted-foreground text-sm leading-relaxed w-full"
                  displayAs="p"
                  placeholder="Краткое описание проекта…"
                  readOnly={ro}
                />
              </div>

              {/* Right: meta */}
              <div className="shrink-0 space-y-2.5 min-w-[180px]">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">ИИ-потенциал</p>
                  <div className="flex items-center gap-1.5">
                    <Bot size={14} style={{ color: AI_COLORS[project.aiReadiness] }} />
                    <InlineText
                      value={project.aiPotential}
                      onSave={v => updateProject(project.id, { aiPotential: v })}
                      className="text-lg font-bold"
                      inputClassName="text-lg font-bold w-24"
                      displayAs="span"
                      readOnly={ro}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <InlineDate value={project.startDate} onSave={v => updateProject(project.id, { startDate: v })} label="Старт" readOnly={ro} />
                  <InlineDate value={project.targetDate} onSave={v => updateProject(project.id, { targetDate: v })} label="Цель" readOnly={ro} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Репозиторий</p>
                  <InlineText
                    value={project.repoUrl}
                    onSave={v => updateProject(project.id, { repoUrl: v })}
                    placeholder="https://github.com/…"
                    className="text-xs text-[var(--nn-blue)] break-all"
                    displayAs="span"
                    readOnly={ro}
                  />
                </div>
              </div>
            </div>

            {/* Progress bar (рассчитывается по плану-графику / дорожной карте) */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5 gap-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Прогресс</span>
                <div className="text-right">
                  <span className="text-sm font-bold tabular-nums">{project.progress}%</span>
                  <p className="text-[10px] text-muted-foreground font-normal normal-case max-w-[220px] leading-tight mt-0.5">
                    Среднее по полям «% выполнения» задач в разделе «План-график» ниже. Пока задач нет — 0%.
                  </p>
                </div>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${project.progress}%`, backgroundColor: progressColor }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Цель проекта (на всю ширину) ── */}
        <Section icon={<Target size={15} />} title="Цель проекта" accent="#0055A5">
          <InlineText
            value={project.goal}
            onSave={v => updateProject(project.id, { goal: v })}
            multiline
            className="text-sm leading-relaxed w-full"
            displayAs="p"
            placeholder="Сформулируйте цель проекта…"
            readOnly={ro}
          />
        </Section>

        {/* ── Метрики + Содержание (две колонки на десктопе) ── */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
          <div className="min-w-0">
            <Section
              icon={<BarChart3 size={14} />}
              title="Ключевые метрики"
              accent="#00A651"
              badge={`${project.kpis.length}`}
              compact
            >
              <KPIPanel
                kpis={project.kpis}
                onAdd={k => addKPI(project.id, k)}
                onUpdate={(id, p) => updateKPI(project.id, id, p)}
                onDelete={id => removeKPI(project.id, id)}
              />
            </Section>
          </div>
          <div className="min-w-0">
            <Section
              icon={<ListChecks size={15} />}
              title="Содержание проекта"
              accent="#0055A5"
              badge={(() => {
                const n = project.scope.length;
                const d = project.scope.filter((s) => s.done).length;
                if (d === 0) return `${n} пунктов`;
                return `${n} пунктов · ${d} выполнено`;
              })()}
            >
              <ScopePanel
                scope={project.scope}
                onAdd={t => addScopeItem(project.id, t)}
                onUpdate={(i, t) => updateScopeItem(project.id, i, t)}
                onDelete={i => removeScopeItem(project.id, i)}
                onReorder={(from, to) => reorderScopeItems(project.id, from, to)}
                onToggleDone={i => toggleScopeItemDone(project.id, i)}
              />
            </Section>
          </div>
        </div>

        {/* ── Documents ── (просмотрщику не показываем) */}
        {canSeeDocuments ? (
          <Section
            icon={<Files size={15} />}
            title="Документы проекта"
            accent="#4A90D9"
            badge={
              project.documents.length === 0
                ? undefined
                : `${project.documents.length}`
            }
          >
            <DocumentsSection
              scope="project"
              storageProjectId={project.id}
              documents={project.documents}
              config={project.documentsConfig ?? defaultProjectDocumentsConfig()}
              onChange={({ documents, config }) =>
                updateProject(project.id, { documents, documentsConfig: config })
              }
              readOnly={ro}
              ganttTasks={project.ganttTasks}
            />
          </Section>
        ) : null}

        {/* ── Risks ── */}
        <Section
          icon={<AlertTriangle size={15} />}
          title="Управление рисками (Agile Risk)"
          accent="#D0021B"
          badge={openRisksCount > 0 ? `${openRisksCount} открытых` : undefined}
        >
          <RiskPanel
            projectId={project.id}
            risks={project.risks}
            onAdd={r => addRisk(project.id, r)}
            onUpdate={(rid, p) => updateRisk(project.id, rid, p)}
            onDelete={rid => removeRisk(project.id, rid)}
          />
        </Section>

        {/* ── Stakeholders ── */}
        <Section icon={<Users size={15} />} title="Стейкхолдеры" accent="#4A90D9" badge={`${project.stakeholders.length}`}>
          <StakeholderPanel
            stakeholders={project.stakeholders}
            onAdd={sh => addStakeholder(project.id, sh)}
            onUpdate={(id, p) => updateStakeholder(project.id, id, p)}
            onDelete={id => removeStakeholder(project.id, id)}
          />
        </Section>

        {/* ── Gantt — на всю ширину колонки (компенсация px-4/px-6 у страницы) ── */}
        <div className="-mx-4 max-w-none sm:-mx-6">
        <Section icon={<CalendarDays size={15} />} title="План-график (Ганта)" accent="#0055A5" badge={`${project.ganttTasks.length} задач`} defaultOpen={false}>
          {showPlanRoadmapMismatch && roadmapEnv && planEnv ? (
            <Alert
              className="relative mb-4 border-amber-200/90 bg-amber-50/60 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/25 dark:text-amber-50"
            >
              <AlertTriangle className="text-amber-600 dark:text-amber-400" />
              <button
                type="button"
                onClick={dismissMismatchBanner}
                aria-label="Скрыть предупреждение"
                title="Скрыть до следующего изменения дат"
                className="absolute right-2 top-2 rounded-md p-1 text-amber-700/70 hover:bg-amber-100/70 hover:text-amber-900 dark:text-amber-200/70 dark:hover:bg-amber-900/40 dark:hover:text-amber-50"
              >
                <X size={14} />
              </button>
              {mainBarCount > 1 ? (
                <>
                  <AlertTitle>Задачи детального плана выходят за границы дорожной карты</AlertTitle>
                  <AlertDescription className="text-amber-900/85 dark:text-amber-100/90">
                    <p>
                      По задачам в этом разделе период{" "}
                      <strong>{formatRuDateRange(planEnv.start, planEnv.end)}</strong>, а объединённый интервал всех
                      полос этой инициативы на главной дорожной карте —{" "}
                      <strong>{formatRuDateRange(roadmapEnv.start, roadmapEnv.end)}</strong>. Часть задач начинается
                      раньше или заканчивается позже этого интервала.
                    </p>
                    <p className="mt-1.5">
                      Отнесите соответствующие задачи к нужной полосе дорожной карты либо сдвиньте сроки здесь или на
                      главной странице (кнопка календаря у названия строки на ганте).
                    </p>
                  </AlertDescription>
                </>
              ) : (
                <>
                  <AlertTitle>Сроки детального плана и общей дорожной карты не совпадают</AlertTitle>
                  <AlertDescription className="text-amber-900/85 dark:text-amber-100/90">
                    <p>
                      По задачам в этом разделе период{" "}
                      <strong>{formatRuDateRange(planEnv.start, planEnv.end)}</strong>, а на главной дорожной карте
                      для этой инициативы задан период{" "}
                      <strong>{formatRuDateRange(roadmapEnv.start, roadmapEnv.end)}</strong>.
                    </p>
                    <p className="mt-1.5">
                      Приведите в соответствие либо задачи здесь, либо сроки полосы на главной странице (кнопка
                      календаря у названия строки на ганте) и при необходимости даты в паспорте («Старт» / «Цель»).
                    </p>
                  </AlertDescription>
                </>
              )}
            </Alert>
          ) : null}
          <EditableGantt
            projectId={project.id}
            tasks={project.ganttTasks}
            documents={project.documents}
            onDocumentsChange={(docs) => updateProject(project.id, { documents: docs })}
            onAdd={t => addGanttTask(project.id, t)}
            onUpdate={(tid, p) => updateGanttTask(project.id, tid, p)}
            onDelete={tid => removeGanttTask(project.id, tid)}
            onReorderTasksByDrag={(activeId, overId) =>
              reorderGanttTasksAfterDrag(project.id, activeId, overId)
            }
            roadmapEnvelope={roadmapEnv}
            mainGanttBarCount={mainBarCount}
          />
        </Section>

        {/* ── Факт: рабочая таблица (Google Sheets) — отдельная секция, открыта по умолчанию ── */}
        {(project.factGanttUrl || ro === false) ? (
          <Section icon={<Table2 size={15} />} title="Факт — рабочая таблица (Google Sheets)" accent="#00A651">
            <FactGanttViewer
              url={project.factGanttUrl}
              canEdit={!ro}
              onChange={(u) => updateProject(project.id, { factGanttUrl: u })}
            />
          </Section>
        ) : null}
        </div>

        {/* ── Канбан-доска ── */}
        <div className="-mx-4 max-w-none sm:-mx-6">
        <Section
          icon={<BookOpen size={15} />}
          title="Канбан"
          accent="#4A90D9"
          badge={`${project.backlog.length} карточек`}
        >
          <KanbanBoard
            projectId={project.id}
            columns={project.kanbanColumns}
            items={project.backlog}
            canEdit={canEdit}
          />
        </Section>
        </div>

      </div>
      </div>
    </div>
  );
}
